import CheckIcon from "@mui/icons-material/Check";
import { Box, Collapse, Stack } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { ReactNode } from "react";

interface SectionProps {
  title?: string;
  children: ReactNode;
  onNext?: () => void;
  onBack?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  completed?: boolean;
  active?: boolean;
  isLast?: boolean;
}

const Section = ({
  title,
  children,
  onNext,
  onBack,
  nextLabel = "Next",
  nextDisabled = false,
  completed = false,
  active = false,
  isLast = false,
}: SectionProps): React.JSX.Element => {
  const getDotStyles = (): { bgcolor: string; border: string } => {
    if (completed) {
      return {
        bgcolor: "#7B1FA2",
        border: "none",
      };
    }
    if (active) {
      return {
        bgcolor: "#7B1FA2",
        border: "none",
      };
    }
    return {
      bgcolor: "text.disabled",
      border: "none",
    };
  };

  return (
    <Box sx={{ display: "flex", mb: active ? 3 : 1 }}>
      {/* Left indicator column */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mr: 2,
        }}
      >
        {/* Status dot */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            ...getDotStyles(),
          }}
        >
          {completed && <CheckIcon sx={{ fontSize: 14, color: "white" }} />}
        </Box>
        {/* Grey vertical border */}
        {!(isLast && !active) && (
          <Box
            sx={{
              width: 2,
              flexGrow: active ? 1 : 0,
              height: active ? "auto" : 16,
              bgcolor: "grey.300",
              mt: 1,
            }}
          />
        )}
      </Box>

      {/* Content column */}
      <Box sx={{ flexGrow: 1 }}>
        {title && (
          <Typography
            variant="body-large"
            color={active ? "textPrimary" : "textSecondary"}
            sx={{ marginTop: "-0.2em" }}
          >
            <strong>{title}</strong>
          </Typography>
        )}
        <Collapse in={active}>
          {children}
          <Stack
            direction="row"
            spacing={2}
            sx={{ mt: 3, justifyContent: "flex-start" }}
          >
            {onBack && (
              <Button variant="tertiary" onClick={onBack}>
                Back
              </Button>
            )}
            {onNext && (
              <Button
                variant="primary"
                onClick={onNext}
                disabled={nextDisabled}
              >
                {nextLabel}
              </Button>
            )}
          </Stack>
        </Collapse>
      </Box>
    </Box>
  );
};

export default Section;
